import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  ApiResponse,
  CopilotWidgetFilters,
  DtVersionSelection,
} from "../types";
import { serializeDtVersions } from "../utils/dtVersions";
import type {
  RoadmapTeam,
  RoadmapTeamCreate,
  RoadmapTopic,
  RoadmapTopicCreate,
  RoadmapTopicUpdate,
  RoadmapItem,
  RoadmapItemCreate,
  RoadmapItemUpdate,
  RoadmapVotes,
  RoadmapComment,
} from "../types";

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only clear token and redirect if this is an authenticated request that failed
      // Don't redirect on login attempts (which shouldn't have tokens anyway)
      const isLoginAttempt =
        error.config?.url?.endsWith("/auth/azure/login") ||
        error.config?.url?.endsWith("/auth/azure/callback");

      if (!isLoginAttempt && localStorage.getItem("token")) {
        // Check if this is a permissions change error
        const errorMessage = error.response?.data?.error || "";
        const isPermissionChange = errorMessage.includes(
          "permissions have changed",
        );

        // Clear token and redirect to login only for authenticated requests
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Show appropriate message based on error type
        if (isPermissionChange) {
          // Store the message to show after redirect
          localStorage.setItem("loginMessage", errorMessage);
        }

        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  },
);

// extractApiError pulls the server-supplied `error` string out of an Axios
// rejection without callers needing inline type assertions or `any`.
export function extractApiError(
  err: unknown,
  fallback = "An unexpected error occurred.",
): string {
  const e = err as
    | { response?: { data?: { error?: string } } }
    | null
    | undefined;
  return e?.response?.data?.error ?? fallback;
}

export function extractApiErrorField(err: unknown): string | null {
  const e = err as
    | { response?: { data?: { data?: { field?: string } } } }
    | null
    | undefined;
  return e?.response?.data?.data?.field ?? null;
}

// Generic API functions
export const apiService = {
  // Auth
  microsoftLogin: (code: string, redirectUri: string) =>
    api.post<ApiResponse>("/auth/microsoft", { code, redirectUri }),

  getProfile: () => api.get<ApiResponse>("/auth/profile"),

  // Users (admin only)
  getUsers: () => api.get<ApiResponse>("/auth/users"),
  getUser: (userId: string) => api.get<ApiResponse>(`/auth/users/${userId}`),
  createUser: (data: any) => api.post<ApiResponse>("/auth/users", data),
  updateUser: (userId: string, data: any) =>
    api.put<ApiResponse>(`/auth/users/${userId}`, data),
  deleteUser: (userId: string) =>
    api.delete<ApiResponse>(`/auth/users/${userId}`),
  grantAdminAccess: (userId: string) =>
    api.post<ApiResponse>(`/auth/users/${userId}/admin`),
  revokeAdminAccess: (userId: string) =>
    api.delete<ApiResponse>(`/auth/users/${userId}/admin`),
  grantUserRole: (userId: string, roleId: string) =>
    api.post<ApiResponse>(`/auth/users/${userId}/roles`, { roleId }),
  revokeUserRole: (userId: string, roleId: string) =>
    api.delete<ApiResponse>(`/auth/users/${userId}/roles/${roleId}`),

  // Products
  getProducts: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/products", { params: filters }),

  getMyProducts: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/products/my", { params: filters }),

  getProduct: (id: string) => api.get<ApiResponse>(`/products/${id}`),

  createProduct: (data: any) => api.post<ApiResponse>("/products", data),

  cloneProduct: (id: string, includeAssessment: boolean = true) =>
    api.post<ApiResponse>(`/products/${id}/clone`, { includeAssessment }),

  updateProduct: (id: string, data: any) =>
    api.put<ApiResponse>(`/products/${id}`, data),

  deleteProduct: (id: string) => api.delete<ApiResponse>(`/products/${id}`),

  approveProduct: (id: string) =>
    api.post<ApiResponse>(`/products/${id}/approve`),

  retireProduct: (id: string) =>
    api.post<ApiResponse>(`/products/${id}/retire`),

  unretireProduct: (id: string, lifecycleStatus?: string) =>
    api.post<ApiResponse>(`/products/${id}/unretire`, {
      lifecycleStatus: lifecycleStatus ?? "",
    }),

  permanentDeleteProduct: (id: string) =>
    api.delete<ApiResponse>(`/products/${id}/permanent`),

  linkProductApplications: (id: string, applicationIds: string[]) =>
    api.post<ApiResponse>(`/products/${id}/applications`, { applicationIds }),

  unlinkProductApplication: (id: string, applicationId: string) =>
    api.delete<ApiResponse>(`/products/${id}/applications/${applicationId}`),

  getDivisionStatistics: () => api.get<ApiResponse>("/products/division-stats"),

  getBusinessAreaStatistics: () => api.get<ApiResponse>("/products/ba-stats"),

  getGlobalStatistics: () => api.get<ApiResponse>("/products/global-stats"),

  getFilterOptions: () => api.get<ApiResponse>("/products/filters"),

  getTagDefinitions: () => api.get<ApiResponse>("/products/tag-definitions"),

  getRelationshipTypes: () =>
    api.get<ApiResponse>("/products/relationship-types"),

  // Ownership requests
  createOwnershipRequest: (productId: string) =>
    api.post<ApiResponse>(`/products/${productId}/ownership-request`),

  getOwnershipRequests: () =>
    api.get<ApiResponse>("/products/ownership-requests"),

  updateOwnershipRequest: (id: string, status: "approved" | "denied") =>
    api.put<ApiResponse>(`/products/ownership-requests/${id}`, { status }),

  // Membership requests
  createMembershipRequest: (data: { businessArea: string; division: string }) =>
    api.post<ApiResponse>("/auth/users/membership-requests", data),

  getMyMembershipRequests: () =>
    api.get<ApiResponse>("/auth/users/membership-requests/me"),

  getMembershipRequests: (status?: "requested" | "approved" | "rejected") =>
    api.get<ApiResponse>("/auth/users/membership-requests", {
      params: status ? { status } : undefined,
    }),

  updateMembershipRequest: (id: string, status: "approved" | "rejected") =>
    api.put<ApiResponse>(`/auth/users/membership-requests/${id}`, { status }),

  // Assessments
  // Section-based CRA schema for the custom (non-SurveyJS) renderer.
  getCRAQuestionsV2: () =>
    api.get<ApiResponse>("/assessments/cra/questions/v2"),

  getSDLQuestions: () => api.get<ApiResponse>("/assessments/sdl/questions"),

  // CRA Assessments
  createCRAAssessment: (productId: string, data: any) =>
    api.post<ApiResponse>(`/assessments/products/${productId}/cra`, data),

  updateCRAAssessment: (assessmentId: string, data: any) =>
    api.put<ApiResponse>(`/assessments/cra/${assessmentId}`, data),

  getCRAAssessment: (assessmentId: string) =>
    api.get<ApiResponse>(`/assessments/cra/${assessmentId}`),

  getLatestCRAAssessment: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/cra`),

  // SDL Assessments
  createSDLAssessment: (productId: string, data: any) =>
    api.post<ApiResponse>(`/assessments/products/${productId}/sdl`, data),

  // Admin SDL Assessment override
  createSDLAssessmentAdminOverride: (productId: string, data: any) =>
    api.post<ApiResponse>(
      `/assessments/products/${productId}/sdl/admin-override`,
      data,
    ),

  updateSDLAssessment: (assessmentId: string, data: any) =>
    api.put<ApiResponse>(`/assessments/sdl/${assessmentId}`, data),

  getSDLAssessment: (assessmentId: string) =>
    api.get<ApiResponse>(`/assessments/sdl/${assessmentId}`),

  getSDLAssessmentsByProduct: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/sdl`),

  getSDLAssessmentByPeriod: (productId: string, period: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/sdl/${period}`),

  getPreviousSubmittedSDLAssessment: (productId: string, period: string) =>
    api.get<ApiResponse>(
      `/assessments/products/${productId}/sdl/${period}/previous`,
    ),

  // Assessment status
  getAssessmentStatus: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/status`),

  // SDL Assessment Periods
  getSDLPeriods: () => api.get<ApiResponse>("/assessments/sdl/periods"),

  createSDLPeriod: (data: {
    year: number;
    quarter: number;
    isOpen?: boolean;
  }) => api.post<ApiResponse>("/assessments/sdl/periods", data),

  toggleSDLPeriod: (id: string) =>
    api.put<ApiResponse>(`/assessments/sdl/periods/${id}/toggle`),

  setCurrentSDLPeriod: (id: string) =>
    api.put<ApiResponse>(`/assessments/sdl/periods/${id}/set-current`),

  deleteSDLPeriod: (id: string) =>
    api.delete<ApiResponse>(`/assessments/sdl/periods/${id}`),

  // SDL Assessment Management
  getSDLAssessmentPeriodsForProduct: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/sdl-periods`),

  submitSDLAssessment: (assessmentId: string) =>
    api.post<ApiResponse>(`/assessments/sdl/${assessmentId}/submit`),

  returnSDLAssessmentToDraft: (assessmentId: string) =>
    api.post<ApiResponse>(`/assessments/sdl/${assessmentId}/return-to-draft`),

  // Data Act Assessments
  getDataActQuestions: () =>
    api.get<ApiResponse>("/assessments/data-act/questions"),

  createDataActAssessment: (productId: string, data: any) =>
    api.post<ApiResponse>(`/assessments/products/${productId}/data-act`, data),

  updateDataActAssessment: (assessmentId: string, data: any) =>
    api.put<ApiResponse>(`/assessments/data-act/${assessmentId}`, data),

  submitDataActAssessment: (assessmentId: string) =>
    api.post<ApiResponse>(`/assessments/data-act/${assessmentId}/submit`),

  getDataActAssessment: (assessmentId: string) =>
    api.get<ApiResponse>(`/assessments/data-act/${assessmentId}`),

  getDataActAssessmentsByProduct: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/data-act`),

  getLatestDataActAssessment: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/data-act/latest`),

  getPreviousDataActAssessment: (productId: string, version: number) =>
    api.get<ApiResponse>(
      `/assessments/products/${productId}/data-act/previous`,
      {
        params: { version },
      },
    ),

  getDataActLinkedCandidates: (
    productId: string,
    mode: "related-service" | "connected-product",
  ) =>
    api.get<ApiResponse>(
      `/assessments/products/${productId}/data-act/linked-candidates`,
      { params: { mode } },
    ),

  returnDataActAssessmentToDraft: (assessmentId: string) =>
    api.post<ApiResponse>(
      `/assessments/data-act/${assessmentId}/return-to-draft`,
    ),

  // Repositories
  getRepositories: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/repositories", { params: filters }),

  getRepository: (owner: string, name: string) =>
    api.get<ApiResponse>(`/repositories/${owner}/${name}`),

  // Applications
  getApplications: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/applications", { params: filters }),

  getApplication: (id: string) => api.get<ApiResponse>(`/applications/${id}`),

  createApplication: (data: any) =>
    api.post<ApiResponse>("/applications", data),

  updateApplication: (id: string, data: any) =>
    api.put<ApiResponse>(`/applications/${id}`, data),

  deleteApplication: (id: string) =>
    api.delete<ApiResponse>(`/applications/${id}`),

  linkApplicationProducts: (id: string, productIds: string[]) =>
    api.post<ApiResponse>(`/applications/${id}/products`, { productIds }),

  unlinkApplicationProduct: (id: string, productId: string) =>
    api.delete<ApiResponse>(`/applications/${id}/products/${productId}`),

  linkApplicationRepositories: (
    id: string,
    repositoryIds: string[],
    role?: string,
  ) =>
    api.post<ApiResponse>(`/applications/${id}/repositories`, {
      repositoryIds,
      role,
    }),

  unlinkApplicationRepository: (id: string, repoId: string) =>
    api.delete<ApiResponse>(`/applications/${id}/repositories/${repoId}`),

  // Application Dependency-Track links
  getApplicationDependencyTrackLinks: (id: string) =>
    api.get<ApiResponse>(`/applications/${id}/dependency-track`),

  linkApplicationDependencyTrackProject: (id: string, projectUuid: string) =>
    api.post<ApiResponse>(`/applications/${id}/dependency-track`, {
      projectUuid,
    }),

  unlinkApplicationDependencyTrackProject: (id: string, linkId: string) =>
    api.delete<ApiResponse>(`/applications/${id}/dependency-track/${linkId}`),

  getApplicationDependencyTrackProjects: (id: string) =>
    api.get<ApiResponse>(`/applications/${id}/dependency-track/projects`),

  getApplicationDependencyTrackFindings: (
    id: string,
    page = 1,
    pageSize = 10,
    severities?: string[],
    versions?: DtVersionSelection,
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (severities && severities.length > 0) {
      params.set("severity", severities.join(","));
    }
    if (versions && Object.keys(versions).length > 0) {
      params.set("versions", serializeDtVersions(versions));
    }
    return api.get<ApiResponse>(
      `/applications/${id}/dependency-track/findings?${params.toString()}`,
    );
  },

  getProductDependencyTrackProjects: (id: string) =>
    api.get<ApiResponse>(`/products/${id}/dependency-track/projects`),

  getProductDependencyTrackFindings: (
    id: string,
    page = 1,
    pageSize = 10,
    severities?: string[],
    versions?: DtVersionSelection,
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (severities && severities.length > 0) {
      params.set("severity", severities.join(","));
    }
    if (versions && Object.keys(versions).length > 0) {
      params.set("versions", serializeDtVersions(versions));
    }
    return api.get<ApiResponse>(
      `/products/${id}/dependency-track/findings?${params.toString()}`,
    );
  },

  // CRA Compliance Assessments
  getCRAComplianceQuestions: () =>
    api.get<ApiResponse>("/assessments/cra-compliance/questions"),

  createCRAComplianceAssessment: (productId: string, data: any) =>
    api.post<ApiResponse>(
      `/assessments/products/${productId}/cra-compliance`,
      data,
    ),

  updateCRAComplianceAssessment: (assessmentId: string, data: any) =>
    api.put<ApiResponse>(`/assessments/cra-compliance/${assessmentId}`, data),

  getCRAComplianceAssessment: (assessmentId: string) =>
    api.get<ApiResponse>(`/assessments/cra-compliance/${assessmentId}`),

  getLatestCRAComplianceAssessment: (productId: string) =>
    api.get<ApiResponse>(`/assessments/products/${productId}/cra-compliance`),

  submitCRAComplianceAssessment: (assessmentId: string) =>
    api.post<ApiResponse>(`/assessments/cra-compliance/${assessmentId}/submit`),

  returnCRAComplianceAssessmentToDraft: (assessmentId: string) =>
    api.post<ApiResponse>(
      `/assessments/cra-compliance/${assessmentId}/return-to-draft`,
    ),

  retakeCRAComplianceAssessment: (assessmentId: string) =>
    api.post<ApiResponse>(`/assessments/cra-compliance/${assessmentId}/retake`),

  // Copy assessment answers
  copyCRAAssessment: (sourceProductId: string, targetProductIds: string[]) =>
    api.post<ApiResponse>(`/assessments/products/${sourceProductId}/cra/copy`, {
      targetProductIds,
    }),

  copySDLAssessment: (
    sourceProductId: string,
    period: string,
    targetProductIds: string[],
  ) =>
    api.post<ApiResponse>(
      `/assessments/products/${sourceProductId}/sdl/${period}/copy`,
      { targetProductIds },
    ),

  copyCRAComplianceAssessment: (
    sourceProductId: string,
    targetProductIds: string[],
  ) =>
    api.post<ApiResponse>(
      `/assessments/products/${sourceProductId}/cra-compliance/copy`,
      { targetProductIds },
    ),

  // CRA Requirement Library CSV — must be downloaded via authenticated fetch
  // (the `<a target=_blank>` approach doesn't attach the bearer token).
  downloadCRARequirementLibraryCSV: async () => {
    const response = await api.get<Blob>(
      "/assessments/cra-compliance/requirement-library.csv",
      { responseType: "blob" },
    );
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cra-requirement-library.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // CRA Economic Operators (per-product)
  listCRAEconomicOperators: (productId: string) =>
    api.get<ApiResponse>(`/products/${productId}/economic-operators`),

  createCRAEconomicOperator: (productId: string, data: any) =>
    api.post<ApiResponse>(`/products/${productId}/economic-operators`, data),

  updateCRAEconomicOperator: (productId: string, opId: string, data: any) =>
    api.patch<ApiResponse>(
      `/products/${productId}/economic-operators/${opId}`,
      data,
    ),

  deleteCRAEconomicOperator: (productId: string, opId: string) =>
    api.delete<ApiResponse>(
      `/products/${productId}/economic-operators/${opId}`,
    ),

  // Company data (unprotected)
  getBusinessAreas: () => api.get<ApiResponse>("/company/business-areas"),
  getDivisions: (businessArea: string) =>
    api.get<ApiResponse>(
      `/company/divisions?businessArea=${encodeURIComponent(businessArea)}`,
    ),

  // Domain lookup (authenticated): used for B2B license persona dispatch
  getDomainInfo: (domain: string) =>
    api.get<ApiResponse>(`/domains/${encodeURIComponent(domain)}`),

  // License requests
  submitLicenseRequest: (
    requestedTier: number,
    resource: string,
    code: boolean,
    chat: boolean,
    design: boolean,
    cowork: boolean,
  ) =>
    api.post<ApiResponse>("/license/requests", {
      requestedTier,
      resource,
      code,
      chat,
      design,
      cowork,
    }),

  getLicenseInbox: () => api.get<ApiResponse>("/license/inbox"),

  decideLicenseRequest: (requestId: string, decision: "approve" | "reject") =>
    api.post<ApiResponse>(`/license/requests/${requestId}/decisions`, {
      decision,
    }),

  // Onboarding requests
  getOnboardingRequests: () => api.get<ApiResponse>("/onboarding-requests"),

  submitOnboardingRequest: (type: string, fields: Record<string, unknown>) =>
    api.post<ApiResponse>("/onboarding-requests", { type, fields }),

  updateOnboardingRequest: (id: string, fields: Record<string, unknown>) =>
    api.put<ApiResponse>(`/onboarding-requests/${id}`, { fields }),

  deleteOnboardingRequest: (id: string) =>
    api.delete<ApiResponse>(`/onboarding-requests/${id}`),

  checkNamespaceAvailability: (name: string) =>
    api.get<ApiResponse>("/onboarding-requests/namespace-availability", {
      params: { name },
    }),

  updateOnboardingRequestStatus: (id: string, status: string) =>
    api.post<ApiResponse>(`/onboarding-requests/${id}/status`, { status }),

  getRoadmapItems: (teamId: string, year: number) =>
    api.get<ApiResponse<{ items: RoadmapItem[] }>>("/roadmap/items", {
      params: { teamId, year },
    }),

  createRoadmapItem: (payload: RoadmapItemCreate) =>
    api.post<ApiResponse<RoadmapItem>>("/roadmap/items", payload),

  getRoadmapItem: (id: string) =>
    api.get<ApiResponse<RoadmapItem>>(
      `/roadmap/items/${encodeURIComponent(id)}`,
    ),

  updateRoadmapItem: (id: string, payload: RoadmapItemUpdate) =>
    api.patch<ApiResponse<RoadmapItem>>(
      `/roadmap/items/${encodeURIComponent(id)}`,
      payload,
    ),

  deleteRoadmapItem: (id: string) =>
    api.delete<ApiResponse<void>>(`/roadmap/items/${encodeURIComponent(id)}`),

  getRoadmapComments: (id: string) =>
    api.get<ApiResponse<{ items: RoadmapComment[] }>>(
      `/roadmap/items/${encodeURIComponent(id)}/comments`,
    ),

  addRoadmapComment: (id: string, body: string) =>
    api.post<ApiResponse<RoadmapComment>>(
      `/roadmap/items/${encodeURIComponent(id)}/comments`,
      { body },
    ),

  deleteRoadmapComment: (id: string, commentId: string) =>
    api.delete<ApiResponse<void>>(
      `/roadmap/items/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
    ),

  updateRoadmapComment: (id: string, commentId: string, body: string) =>
    api.patch<ApiResponse<RoadmapComment>>(
      `/roadmap/items/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
      { body },
    ),

  getRoadmapVotes: (id: string) =>
    api.get<ApiResponse<RoadmapVotes>>(
      `/roadmap/items/${encodeURIComponent(id)}/votes`,
    ),

  setRoadmapVote: (id: string, value: RoadmapVotes["myVote"]) =>
    api.put<ApiResponse<RoadmapVotes>>(
      `/roadmap/items/${encodeURIComponent(id)}/votes`,
      { value },
    ),

  getRoadmapTeams: () =>
    api.get<ApiResponse<{ items: RoadmapTeam[] }>>("/roadmap/teams"),

  createRoadmapTeam: (payload: RoadmapTeamCreate) =>
    api.post<ApiResponse<RoadmapTeam>>("/roadmap/teams", payload),

  deleteRoadmapTeam: (id: string) =>
    api.delete<ApiResponse>(`/roadmap/teams/${encodeURIComponent(id)}`),

  addRoadmapTeamMember: (id: string, email: string) =>
    api.post<ApiResponse>(`/roadmap/teams/${encodeURIComponent(id)}/members`, {
      email,
    }),

  removeRoadmapTeamMember: (id: string, email: string) =>
    api.delete<ApiResponse>(
      `/roadmap/teams/${encodeURIComponent(id)}/members/${encodeURIComponent(email)}`,
    ),

  getRoadmapTopics: (teamId?: string) =>
    api.get<ApiResponse<{ items: RoadmapTopic[] }>>("/roadmap/topics", {
      params: teamId ? { teamId } : {},
    }),

  createRoadmapTopic: (payload: RoadmapTopicCreate) =>
    api.post<ApiResponse<RoadmapTopic>>("/roadmap/topics", payload),

  updateRoadmapTopic: (id: string, payload: RoadmapTopicUpdate) =>
    api.patch<ApiResponse<RoadmapTopic>>(
      `/roadmap/topics/${encodeURIComponent(id)}`,
      payload,
    ),

  deleteRoadmapTopic: (id: string) =>
    api.delete<ApiResponse>(`/roadmap/topics/${encodeURIComponent(id)}`),

  // Feature requests
  submitFeatureRequest: (
    title: string,
    description: string,
    category: string,
  ) =>
    api.post<ApiResponse>("/feature-requests", {
      title,
      description,
      category,
    }),

  updateOwnFeatureRequest: (id: string, title: string, description: string) =>
    api.put<ApiResponse>(`/feature-requests/${id}`, { title, description }),

  getFeatureRequests: () => api.get<ApiResponse>("/feature-requests"),

  getFeatureRequestTopics: () =>
    api.get<ApiResponse>("/feature-requests/topics"),

  createFeatureRequestTopic: (name: string, teamId?: string | null) =>
    api.post<ApiResponse>("/feature-requests/topics", { name, teamId }),

  updateFeatureRequestTopic: (slug: string, name: string, color: string) =>
    api.patch<ApiResponse>(
      `/feature-requests/topics/${encodeURIComponent(slug)}`,
      { name, color },
    ),

  assignFeatureRequestTopicTeam: (slug: string, teamId: string | null) =>
    api.put<ApiResponse>(
      `/feature-requests/topics/${encodeURIComponent(slug)}/team`,
      { teamId },
    ),

  getFeatureRequestTeams: () => api.get<ApiResponse>("/feature-requests/teams"),

  createFeatureRequestTeam: (name: string) =>
    api.post<ApiResponse>("/feature-requests/teams", { name }),

  deleteFeatureRequestTeam: (id: string) =>
    api.delete<ApiResponse>(`/feature-requests/teams/${id}`),

  addFeatureRequestTeamMember: (id: string, email: string) =>
    api.post<ApiResponse>(`/feature-requests/teams/${id}/members`, { email }),

  removeFeatureRequestTeamMember: (id: string, email: string) =>
    api.delete<ApiResponse>(
      `/feature-requests/teams/${id}/members/${encodeURIComponent(email)}`,
    ),

  deleteFeatureRequestTopic: (slug: string, reassign: boolean) =>
    api.delete<ApiResponse>(
      `/feature-requests/topics/${encodeURIComponent(slug)}`,
      { params: reassign ? { reassign: "true" } : {} },
    ),

  getFeatureRequestPowerUsers: () =>
    api.get<ApiResponse>("/feature-requests/power-users"),

  addFeatureRequestPowerUser: (email: string) =>
    api.post<ApiResponse>("/feature-requests/power-users", { email }),

  removeFeatureRequestPowerUser: (id: string) =>
    api.delete<ApiResponse>(`/feature-requests/power-users/${id}`),

  getSimilarFeatureRequests: (q: string, desc?: string) =>
    api.get<ApiResponse>("/feature-requests/similar", {
      params: { q, ...(desc ? { desc } : {}) },
    }),

  toggleFeatureRequestVote: (id: string) =>
    api.post<ApiResponse>(`/feature-requests/${id}/vote`),

  getFeatureRequestComments: (id: string) =>
    api.get<ApiResponse>(`/feature-requests/${id}/comments`),

  addFeatureRequestComment: (id: string, body: string) =>
    api.post<ApiResponse>(`/feature-requests/${id}/comments`, { body }),

  deleteFeatureRequestComment: (id: string, commentId: string) =>
    api.delete<ApiResponse>(`/feature-requests/${id}/comments/${commentId}`),

  updateFeatureRequest: (
    id: string,
    patch: {
      etaQuarter?: string;
      officialResponse?: string;
      category?: string;
    },
  ) => api.patch<ApiResponse>(`/feature-requests/${id}`, patch),

  setFeatureRequestStatus: (id: string, status: string) =>
    api.post<ApiResponse>(`/feature-requests/${id}/status`, { status }),

  mergeFeatureRequest: (id: string, targetId: string) =>
    api.post<ApiResponse>(`/feature-requests/${id}/merge`, { targetId }),

  deleteFeatureRequest: (id: string) =>
    api.delete<ApiResponse>(`/feature-requests/${id}`),

  // Roles data (unprotected)
  getRoles: () => api.get<ApiResponse>("/roles"),

  // GitHub
  checkCopilotAccess: () =>
    api.get<ApiResponse<{ hasAccess: boolean }>>("/github/copilot/access"),
  getCopilotWidget: (widgetId: string, filters?: CopilotWidgetFilters) =>
    api.get<ApiResponse<unknown>>(`/github/copilot/widget/${widgetId}`, {
      params: filters,
    }),

  // Billing
  getInvoices: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/billing/invoices", { params: filters }),
  getInvoiceSummary: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/billing/summary", { params: filters }),
  getUsageCaps: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/billing/usage-caps", { params: filters }),
  getLicenseRequests: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/billing/license-requests", { params: filters }),

  // DT API Keys
  getDtAPIKeys: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/dt-api-keys", { params: filters }),

  getDtAPIKey: (id: string) => api.get<ApiResponse>(`/dt-api-keys/${id}`),

  createDtAPIKey: (data: {
    owner: string;
    prefixes: string[];
    ownerIds: string[];
  }) => api.post<ApiResponse>("/dt-api-keys", data),

  updateDtAPIKey: (
    id: string,
    data: {
      owner?: string;
      isActive?: boolean;
      prefixes?: string[];
      ownerIds?: string[];
    },
  ) => api.put<ApiResponse>(`/dt-api-keys/${id}`, data),

  deleteDtAPIKey: (id: string) => api.delete<ApiResponse>(`/dt-api-keys/${id}`),

  getDtAPIKeyPrefixes: () => api.get<ApiResponse>("/dt-api-keys/prefixes"),

  // Security Advisories
  getSecurityAdvisories: (filters?: Record<string, any>) =>
    api.get<ApiResponse>("/security-advisories", { params: filters }),

  getSecurityAdvisory: (id: string) =>
    api.get<ApiResponse>(`/security-advisories/${id}`),

  createSecurityAdvisory: (data: {
    productId?: string;
    title: string;
    businessArea?: string;
    division?: string;
    status?: "draft" | "final";
    brandKey?: string;
    csaf: Record<string, unknown>;
  }) => api.post<ApiResponse>("/security-advisories", data),

  updateSecurityAdvisory: (
    id: string,
    data: {
      title?: string;
      status?: "draft" | "final";
      brandKey?: string;
      csaf?: Record<string, unknown>;
    },
  ) => api.put<ApiResponse>(`/security-advisories/${id}`, data),

  deleteSecurityAdvisory: (id: string) =>
    api.delete<ApiResponse>(`/security-advisories/${id}`),

  exportSecurityAdvisoryCsaf: (id: string) =>
    api.get(`/security-advisories/${id}/export/csaf`, {
      responseType: "blob",
    }),

  // ── Assessment Import ──

  importAssessment: (assessmentType: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<ApiResponse>(
      `/assessments/${assessmentType}/import`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
  },

  getImportTemplateUrl: (assessmentType: string) =>
    `/api/assessments/${assessmentType}/import-template`,

  // Threat Models
  getThreatModels: (params?: Record<string, unknown>) =>
    api.get<ApiResponse>("/threat-models", { params }),

  getThreatModel: (id: string) => api.get<ApiResponse>(`/threat-models/${id}`),

  createThreatModel: (data: {
    name: string;
    description?: string;
    product_id?: string;
    application_id?: string;
    config: string;
    version?: string;
  }) => api.post<ApiResponse>("/threat-models", data),

  getThreatModelFamilies: (params?: {
    search?: string | undefined;
    phase?: string | undefined;
    include_archived?: boolean | undefined;
    page?: number;
    page_size?: number;
  }) => api.get<ApiResponse>("/threat-models/families", { params }),

  getThreatModelVersions: (id: string, includeArchived = false) =>
    api.get<ApiResponse>(`/threat-models/${id}/versions`, {
      params: includeArchived ? { include_archived: true } : undefined,
    }),

  createThreatModelVersion: (
    id: string,
    data: { version: string; mode: string; summary?: string },
  ) => api.post<ApiResponse>(`/threat-models/${id}/versions`, data),

  archiveThreatModel: (id: string) =>
    api.post<ApiResponse>(`/threat-models/${id}/archive`),

  unarchiveThreatModel: (id: string) =>
    api.post<ApiResponse>(`/threat-models/${id}/unarchive`),

  pinThreatModelVersion: (id: string, productId: string) =>
    api.put<ApiResponse>(`/threat-models/${id}/product-pin`, {
      product_id: productId,
    }),

  updateThreatModel: (
    id: string,
    data: {
      name?: string;
      description?: string;
      phase?: string;
      config?: unknown;
      product_id?: string;
      application_id?: string;
    },
  ) => api.put<ApiResponse>(`/threat-models/${id}`, data),

  deleteThreatModel: (id: string) =>
    api.delete<ApiResponse>(`/threat-models/${id}`),

  resetThreatModel: (id: string) =>
    api.post<ApiResponse>(`/threat-models/${id}/reset`),

  getThreatModelSummary: (id: string) =>
    api.get<ApiResponse>(`/threat-models/${id}/summary`),

  getActiveArtifact: (id: string) =>
    api.get<ApiResponse>(`/threat-models/${id}/active-artifact`),

  getArtifact: (id: string, filename: string) =>
    api.get<ApiResponse>(`/threat-models/${id}/artifacts/${filename}`),

  putArtifact: (
    id: string,
    filename: string,
    content: string,
    contentType?: string,
  ) =>
    api.put<ApiResponse>(`/threat-models/${id}/artifacts/${filename}`, {
      content,
      content_type: contentType || "application/json",
    }),

  triggerStage: (id: string, data: { stage?: string; autonomous?: boolean }) =>
    api.post<ApiResponse>(`/threat-models/${id}/trigger`, data),

  getSessionPrompts: (id: string) =>
    api.get<ApiResponse>(`/threat-models/${id}/prompts`),

  upsertSessionPrompt: (id: string, slug: string, content: string) =>
    api.put<ApiResponse>(`/threat-models/${id}/prompts/${slug}`, {
      prompt_slug: slug,
      content,
    }),

  deleteSessionPrompt: (id: string, slug: string) =>
    api.delete<ApiResponse>(`/threat-models/${id}/prompts/${slug}`),
};

export default api;
